import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, Check, Shield, Clock } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import CTAButtons from '../components/CTAButtons';
import RegistrationModal from '../components/RegistrationModal';

const TrademarkSearch = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const packages = [
    {
      name: 'Basic Search',
      price: 99,
      features: [
        'USPTO Database Search',
        'Identical Mark Analysis',
        'Basic Similarity Report',
        'Search Results within 24 hours',
        'Email Support'
      ]
    },
    {
      name: 'Comprehensive Search',
      price: 199,
      featured: true,
      features: [
        'Everything in Basic Search',
        'State & Common Law Search',
        'International Database Search',
        'Detailed Legal Opinion',
        'Risk Assessment Report',
        'Phone & Email Support',
        'Attorney Consultation Included'
      ]
    },
    {
      name: 'Premium Search & Watch',
      price: 299,
      features: [
        'Everything in Comprehensive Search',
        '6-Month Trademark Monitoring',
        'Monthly Watch Reports',
        'Conflict Alert Notifications',
        'Priority Phone Support',
        'Strategy Consultation'
      ]
    }
  ];

  return (
    <div>
      <Helmet>
        <title>Comprehensive Trademark Search Services | Global Trademark USA</title>
        <meta name="description" content="Professional trademark search services to identify potential conflicts before filing. Comprehensive database searches with detailed reports and expert analysis." />
        <meta name="keywords" content="trademark search, USPTO search, trademark availability, brand name search, trademark conflict analysis" />
        <link rel="canonical" href="https://yourdomain.com/trademark-search" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-white to-blue-50 py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-xl font-semibold">
              Trademark Search
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8">
              Comprehensive <span className="text-purple-600">Trademark Search</span>
              <br />Before You File
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
              Avoid costly conflicts and rejections. Our professional trademark search services identify potential issues before you invest in your application.
            </p>
            <CTAButtons buttonText="Start My Search" />
          </div>
        </div>
      </section>

      {/* Why Search First Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Search Before Filing?
            </h2>
            <p className="text-xl text-gray-600">
              A professional trademark search can save you time, money, and legal complications
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Avoid Rejections</h3>
              <p className="text-base text-gray-600">
                Identify potential conflicts that could lead to application rejection or costly opposition proceedings.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Save Time & Money</h3>
              <p className="text-base text-gray-600">
                Prevent wasted filing fees and delays by ensuring your mark is available before you apply.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Professional Analysis</h3>
              <p className="text-base text-gray-600">
                Get expert legal opinions on the registrability and strength of your proposed trademark.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Search Package
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional trademark search services with varying levels of coverage
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <Card key={index} className={`relative ${pkg.featured ? 'border-purple-300 shadow-xl scale-105' : 'border-gray-200'} h-[473px] flex flex-col`}>
                {pkg.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-1 rounded-full text-xl font-semibold">
                    Most Popular
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                  <div className="text-3xl font-bold text-gray-900">
                    ${pkg.price}
                  </div>
                </CardHeader>
                <CardContent className="flex-1 pb-3">
                  <div className="max-h-[240px] overflow-y-auto pr-2">
                    <ul className="feature-list space-y-0">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx}>
                          <Check className="w-5 h-5 text-purple-600" />
                          <span className="text-base text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={() => setIsModalOpen(true)} 
                    className={`w-full ${
                      pkg.featured 
                        ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800' 
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}
                  >
                    Start My Search
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Search Process
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive analysis to give you confidence in your trademark application
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-semibold text-xl">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Federal Database Search</h3>
                    <p className="text-base text-gray-600">
                      Comprehensive search of the USPTO trademark database for identical and similar marks.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-semibold text-xl">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">State & Common Law Search</h3>
                    <p className="text-base text-gray-600">
                      Search state registrations and common law rights that might conflict with your mark.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-semibold text-xl">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Legal Analysis</h3>
                    <p className="text-base text-gray-600">
                      Expert attorney review and legal opinion on registrability and potential conflicts.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-semibold text-xl">4</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Detailed Report</h3>
                    <p className="text-base text-gray-600">
                      Comprehensive report with findings, risk assessment, and strategic recommendations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Get Peace of Mind Before Filing
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              Our professional trademark search can save you thousands in potential conflicts and rejections.
            </p>
            <CTAButtons buttonText="Start My Trademark Search" variant="dark" />
          </div>
        </div>
      </section>

      <RegistrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
};

export default TrademarkSearch;
